import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import {
  ClosingFundingDatesFormFieldId,
  FundingInformationFieldId,
  WireToInformationFieldId,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.constants';
import {
  buildBrokerApplicationFields,
  buildDealerApplicationFields,
  buildInvestorApplicationFields,
  getAllClosingAttendantCompanies,
  getIsAnyClosingCompanySectionChanged,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.manufacturedHomeContacts.utils';
import {
  buildAttorneyApplicationFields,
  buildClosingPayload,
  buildCompanyApplicationFields,
  buildFundingApplicationData,
  buildFundingPayload,
  checkIsPrepaidInterestCreditDataChanged,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.utils';
import { ADVANCE_AMOUNT_FIELDS } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/hooks/useFundingHandlers/constants';
import { calculateAdvanceAmountPercent } from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/FundingInformation/FundingInformation.utils';
import _ from 'lodash';

import { EMPTY_GUID } from 'constants/data';
import { getConditionalObject } from 'utils/data';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import { findClosingCompany } from './LoanEditorCustomFormDrawer.closing.utils';
import { SAVE_COMPANY_KEY_BY_TYPE } from './LoanEditorCustomFormDrawer.constants';

const LOCK_GATED_CLOSING_FIELD_IDS = Object.values(ClosingFundingDatesFormFieldId);

const FUNDING_SELECT_ID_FIELD_IDS = [
  FundingInformationFieldId.WAREHOUSE_BANK_ID,
  WireToInformationFieldId.WAREHOUSE_BANK_ID,
  WireToInformationFieldId.FEE_PROVIDER_ID,
];

const checkHasAttendantCompanyLocationId = ({ attendantCompanyLocationId }) =>
  !_.isNil(attendantCompanyLocationId) && attendantCompanyLocationId !== EMPTY_GUID;

// The native closing builders read the location reference from closingCompanyLocationId, which is the
// loan's own location row id; the server stores the request value as the fee provider location link.
const withStoredAttendantCompanyLocation = company =>
  company && checkHasAttendantCompanyLocationId(company)
    ? { ...company, closingCompanyLocationId: company.attendantCompanyLocationId }
    : company;

const buildSaveCompanies = closingCompanies =>
  _.reduce(
    SAVE_COMPANY_KEY_BY_TYPE,
    (acc, companyKey, closingCompanyType) => {
      acc[companyKey] = withStoredAttendantCompanyLocation(
        findClosingCompany({ closingCompanies, closingCompanyType }),
      );

      return acc;
    },
    {},
  );

export const buildClosingApplicationData = ({ closingInfo, closingCompanies }) => {
  if (!closingInfo) {
    return {};
  }

  const {
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    brokerCompany,
    dealerCompany,
    investorCompany,
  } = buildSaveCompanies(closingCompanies);

  return {
    ...closingInfo,
    ...buildCompanyApplicationFields(titleCompany, 'title'),
    ...buildCompanyApplicationFields(escrowCompany, 'escrow'),
    ...buildAttorneyApplicationFields(sellerAttorney, 'sellerAttorney'),
    ...buildAttorneyApplicationFields(buyerAttorney, 'buyerAttorney'),
    ...buildBrokerApplicationFields(brokerCompany),
    ...buildDealerApplicationFields(dealerCompany),
    ...buildInvestorApplicationFields(investorCompany),
    ...closingInfo.prepaidInterestCreditData,
    approvalDate: closingInfo.approvalDate,
    approvalExpirationDate: closingInfo.approvalExpirationDate,
    maturityDate: closingInfo.maturityDate,
  };
};

export const buildDrawerFundingApplicationData = fundingInfo =>
  _.isEmpty(fundingInfo) ? {} : buildFundingApplicationData(fundingInfo);

export const pickChangedEntityValues = ({ formState, formPendingChanges, formPath }) =>
  _.pickBy(formState?.[formPath], (__, fieldName) =>
    checkIsPendingChangeHasDifference(formPendingChanges?.[formPath]?.[fieldName]),
  );

export const buildClosingSaveRequest = ({
  storedClosingData,
  changedValues,
  closingCompanies,
  hasInterestCredit,
  loanApplication,
  subjectPropertyState,
  businessDays,
  isLockGatedFieldsDisabled,
}) => {
  const formData = {
    ...storedClosingData,
    ...(isLockGatedFieldsDisabled
      ? _.omit(changedValues, LOCK_GATED_CLOSING_FIELD_IDS)
      : changedValues),
  };
  const companies = buildSaveCompanies(closingCompanies);
  const isCompanyChanged = getIsAnyClosingCompanySectionChanged({
    formData,
    initialFormData: storedClosingData,
    ...companies,
  });

  return {
    ...buildClosingPayload(formData, {
      hasInterestCredit,
      loanApplication,
      subjectPropertyState,
      businessDays,
      includeLockGatedFundingData: true,
      includePrepaidInterestCreditData:
        !isLockGatedFieldsDisabled &&
        checkIsPrepaidInterestCreditDataChanged({
          currentFormData: formData,
          initialFormData: storedClosingData,
        }),
    }),
    ...getConditionalObject(isCompanyChanged, () => ({
      closingAttendantCompanies: getAllClosingAttendantCompanies({ formData, ...companies }),
    })),
    requestMetadata: { tabId: LoanApplicationTabId.CLOSING },
  };
};

const toSelectOption = value => (_.isNil(value) || _.isObject(value) ? value : { value });

const getAdvanceAmountPercentage = ({ formData, changedValues }) =>
  Object.keys(changedValues).some(fieldId => ADVANCE_AMOUNT_FIELDS.has(fieldId))
    ? calculateAdvanceAmountPercent({
        advanceAmount: formData[WireToInformationFieldId.ADVANCE_AMOUNT],
        addAdvanceAmount: formData[WireToInformationFieldId.ADD_ADVANCE_AMOUNT],
        wireAmount: formData[WireToInformationFieldId.WIRE_AMOUNT],
      })
    : formData[WireToInformationFieldId.ADVANCE_AMOUNT_PERCENT];

export const buildFundingSaveRequest = ({ storedFundingData, changedValues }) => {
  const storedFormData = {
    ...storedFundingData,
    ..._.mapValues(_.pick(storedFundingData, FUNDING_SELECT_ID_FIELD_IDS), toSelectOption),
  };
  const formData = { ...storedFormData, ...changedValues };

  return {
    ...buildFundingPayload({
      ...formData,
      [WireToInformationFieldId.ADVANCE_AMOUNT_PERCENT]: getAdvanceAmountPercentage({
        formData,
        changedValues,
      }),
    }),
    tabId: LoanApplicationTabId.CLOSING,
  };
};
